import React, { Component } from "react";

// CSS
import "../../styles/newDesign.scss";
import "./Courses.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";

// Helper
import { resolveTenant, getCourse, requestSubscription, isLoggedIn, saveCourseProgress } from "../../Helper/Api";
import VideoPlayer from "./VideoPlayer";
import Model3DViewer from "./Model3DViewer";

const properties = require("../../properties.json");

const ITEM_ICON = {
  VIDEO: "fa-play-circle",
  AUDIO: "fa-volume-up",
  IMAGE: "fa-image",
  DOCUMENT: "fa-file-alt",
  MODEL_3D: "fa-cube",
};

class CourseDetail extends Component {
  audioRef = React.createRef();
  audioProgressTimer = null;

  state = {
    tenant: null,
    notFound: false,
    course: null,
    subscribing: false,
    msg: null,
    playingItem: null,
    resumeSeconds: null,
  };

  componentDidMount() {
    const id = this.props.match.params.id;
    resolveTenant()
      .then((tenant) => {
        this.setState({ tenant });
        return getCourse(id);
      })
      .then((course) => {
        document.title = properties.site_name + " - " + course.title;
        this.setState({ course });
        // Resume where the student left off (see CourseResponse.lastItemId/lastPositionSeconds) --
        // find the item across every section rather than opening it via openItem, since that would
        // reset the tracked position back to the start.
        if (course.lastItemId) {
          const lastItem = (course.sections || [])
            .flatMap((s) => s.items || [])
            .find((i) => i.id === course.lastItemId);
          if (lastItem && !lastItem.locked && (lastItem.contentUrl || lastItem.hlsManifestUrl)) {
            this.setState({ playingItem: lastItem, resumeSeconds: course.lastPositionSeconds || null });
          }
        }
      })
      .catch(() => this.setState({ notFound: true }));
  }

  componentWillUnmount() {
    this.stopAudioProgressReporting();
  }

  reportProgress = (itemId, positionSeconds) => {
    const { course } = this.state;
    if (course) saveCourseProgress(course.id, itemId, positionSeconds);
  };

  stopAudioProgressReporting = () => {
    if (this.audioProgressTimer) {
      clearInterval(this.audioProgressTimer);
      this.audioProgressTimer = null;
    }
    const audio = this.audioRef.current;
    if (audio && this.state.playingItem) {
      this.reportProgress(this.state.playingItem.id, Math.floor(audio.currentTime));
    }
  };

  startAudioProgressReporting = (itemId) => {
    this.audioProgressTimer = setInterval(() => {
      const audio = this.audioRef.current;
      if (audio && !audio.paused && !audio.seeking) {
        this.reportProgress(itemId, Math.floor(audio.currentTime));
      }
    }, 10000);
  };

  subscribe = () => {
    if (!isLoggedIn()) {
      window.location.href = "/sign-in";
      return;
    }
    const { course, tenant } = this.state;
    this.setState({ subscribing: true, msg: null });
    requestSubscription({ periodId: course.periodId, branchId: tenant.branchId })
      .then(() => {
        this.setState({ subscribing: false, msg: { text: "Subscription request sent. The teacher will review it soon.", type: "success" } });
      })
      .catch((error) => {
        this.setState({ subscribing: false, msg: { text: error.message || "Could not send the request.", type: "warning" } });
      });
  };

  openItem = (item) => {
    if (item.locked || !(item.contentUrl || item.hlsManifestUrl)) return;
    this.stopAudioProgressReporting();
    if (item.itemType === "DOCUMENT") {
      window.open(item.contentUrl, "_blank", "noreferrer");
      this.reportProgress(item.id, null);
      return;
    }
    // A fresh click always starts from the top -- resuming mid-item only happens via the
    // auto-selected lastItemId path in componentDidMount, never from clicking it again.
    this.setState({ playingItem: item, resumeSeconds: null });
    this.reportProgress(item.id, item.itemType === "VIDEO" || item.itemType === "AUDIO" ? 0 : null);
  };

  renderFrame() {
    const { playingItem, course, resumeSeconds } = this.state;

    if (!playingItem) {
      return (
        <div className="player-frame-placeholder">
          {course.image ? <img src={course.image} alt={course.title} /> : <i className="fas fa-book-open"></i>}
          <p>Select an item from the course content below to view it here</p>
        </div>
      );
    }
    if (playingItem.itemType === "VIDEO") {
      return (
        <VideoPlayer
          key={playingItem.id}
          itemId={playingItem.id}
          src={playingItem.contentUrl}
          hlsManifestUrl={playingItem.hlsManifestUrl}
          viewerLabel={course.viewerLabel}
          resumeSeconds={resumeSeconds}
          onProgress={(seconds) => this.reportProgress(playingItem.id, seconds)}
        />
      );
    }
    if (playingItem.itemType === "AUDIO") {
      return (
        <div className="audio-player-frame">
          <i className="fas fa-volume-up"></i>
          <p>{playingItem.title}</p>
          <audio
            key={playingItem.id}
            ref={this.audioRef}
            src={playingItem.contentUrl}
            controls
            controlsList="nodownload noremoteplayback"
            disableRemotePlayback
            onContextMenu={(e) => e.preventDefault()}
            autoPlay
            style={{ width: "100%" }}
            onLoadedMetadata={(e) => {
              if (resumeSeconds) e.target.currentTime = resumeSeconds;
              this.startAudioProgressReporting(playingItem.id);
            }}
          />
        </div>
      );
    }
    if (playingItem.itemType === "IMAGE") {
      return (
        <img
          className="image-player-frame"
          src={playingItem.contentUrl}
          alt={playingItem.title}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
      );
    }
    if (playingItem.itemType === "MODEL_3D") {
      return <Model3DViewer key={playingItem.id} src={playingItem.contentUrl} />;
    }
    return null;
  }

  render() {
    const { tenant, notFound, course, subscribing, msg } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>No page was found for this domain.</p>
          </div>
        </div>
      );
    }

    if (!tenant || !course) return null;

    return (
      <>
        <Header tenant={tenant} />

        <div className="nd-page">
          <div className="nd-hero">
            <div className="nd-wrap">
              <div className="nd-eyebrow">Course</div>
              <h1>{course.title}</h1>
            </div>
          </div>

          <div className="nd-section nd-wrap">
            <div className="course-detail-page">
              <div>
                <div className="player-frame">{this.renderFrame()}</div>

                {course.description && <p className="course-full-desc">{course.description}</p>}

                {(course.sections || []).map((section) => (
                  <div className="course-section" key={section.id}>
                    <h4>{section.title}</h4>
                    <div className="course-items">
                      {(section.items || []).map((item) => (
                        <div
                          className={`course-item ${item.locked ? "locked" : "playable"} ${this.state.playingItem && this.state.playingItem.id === item.id ? "active" : ""}`}
                          key={item.id}
                          onClick={() => this.openItem(item)}
                        >
                          <i className={`fas ${item.locked ? "fa-lock" : ITEM_ICON[item.itemType] || "fa-play-circle"}`}></i>
                          <span>{item.title}</span>
                          {item.isFreePreview && !item.locked && <span className="preview-tag">Free preview</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <div className="subscribe-box">
                  {course.image && <img src={course.image} alt={course.title} />}
                  {msg && <div className={`crs-msg ${msg.type}`}>{msg.text}</div>}
                  {course.enrolled ? (
                    <p className="already-enrolled">You are enrolled in this course</p>
                  ) : (
                    <button type="button" className={`crs-enroll ${subscribing ? "loading" : ""}`} disabled={subscribing} onClick={this.subscribe}>
                      {subscribing ? "Sending..." : "Request subscription"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default CourseDetail;
